import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Table, Button, Modal, Form, Alert } from "react-bootstrap";
import ReactQuill, { Quill } from "react-quill";
import ImageResize from "quill-image-resize-module-react";
import "react-quill/dist/quill.snow.css";
import {
  getAllBerita,
  createBerita,
  updateBerita,
  deleteBerita,
  uploadBeritaImage,
  BASE_URL,
  getBeritaUploadImageUrl,
} from "../../services/api";
import { getStoredUser } from "../../utils/authStorage";
import "./BeritaManagement.css";
import "./Dashboard.css";

// Register Image Resize module for resizing images
Quill.register("modules/imageResize", ImageResize);

const BeritaManagement = () => {
  const [beritaList, setBeritaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentBerita, setCurrentBerita] = useState({
    id: null,
    judul: "",
    ringkasan: "",
    isiberita: "",
    foto: "",
    jenisberita: "berita",
    judul_en: "",
    ringkasan_en: "",
    isiberita_en: "",
  });
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);
  const [htmlSource, setHtmlSource] = useState("");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);

  // Refs
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);

  // Quill modules configuration
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["link", "image", "video"],
        ["clean"],
        ["code-block"],
      ],
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize", "Toolbar"],
      },
      clipboard: {
        matchVisual: true,
      },
    }),
    [],
  );

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "video",
    "code-block",
    "width",
    "height",
    "style",
  ];

  useEffect(() => {
    fetchBerita();
  }, []);

  const fetchBerita = async () => {
    try {
      console.log("Fetching berita list...");
      const response = await getAllBerita();
      if (response.data.success) {
        const filtered = response.data.data.filter(
          (item) => item.jenisberita?.toLowerCase() === "berita",
        );
        console.log("Berita list fetched:", filtered.length, "items");
        console.log("First berita foto:", filtered[0]?.foto);
        setBeritaList(filtered);
      }
    } catch (error) {
      console.error("Error fetching berita:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowModal = (berita = null) => {
    if (berita) {
      console.log("Opening edit modal for berita:", berita);
      console.log("Berita foto field:", berita.foto);
      setEditMode(true);
      setCurrentBerita({
        ...berita,
        judul_en: berita.judul_en || "",
        ringkasan_en: berita.ringkasan_en || "",
        isiberita_en: berita.isiberita_en || "",
      });
      // Set thumbnail preview if exists
      if (berita.foto) {
        const previewUrl = `${BASE_URL}/uploads/${berita.foto}`;
        console.log("Setting thumbnail preview:", previewUrl);
        setThumbnailPreview(previewUrl);
      }
    } else {
      console.log("Opening new berita modal");
      setEditMode(false);
      setCurrentBerita({
        id: null,
        judul: "",
        ringkasan: "",
        isiberita: "",
        foto: "",
        jenisberita: "berita",
        judul_en: "",
        ringkasan_en: "",
        isiberita_en: "",
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSubmitting(false);
    setCurrentBerita({
      id: null,
      judul: "",
      ringkasan: "",
      isiberita: "",
      foto: "",
      jenisberita: "berita",
      judul_en: "",
      ringkasan_en: "",
      isiberita_en: "",
    });
    setAlert({ show: false, type: "", message: "" });
    setShowHtmlSource(false);
    setHtmlSource("");
    setThumbnailFile(null);
    setThumbnailPreview("");
  };

  const handleChange = (e) => {
    setCurrentBerita((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEditorChange = (content) => {
    setCurrentBerita((prev) => ({
      ...prev,
      isiberita: content,
    }));
  };

  const handleEditorChangeEn = (content) => {
    setCurrentBerita((prev) => ({
      ...prev,
      isiberita_en: content,
    }));
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setAlert({
        show: true,
        type: "danger",
        message: "Hanya file gambar yang diperbolehkan!",
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        show: true,
        type: "danger",
        message: "Ukuran file maksimal 5MB!",
      });
      return;
    }

    setUploadingImage(true);
    setAlert({ show: false, type: "", message: "" });

    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await uploadBeritaImage(formData);

      if (response.data.success) {
        // Get Quill editor instance
        const quill = quillRef.current?.getEditor();
        if (quill) {
          // Get current cursor position
          const range = quill.getSelection(true);
          // Insert image at cursor position
          quill.insertEmbed(range.index, "image", getBeritaUploadImageUrl(response.data.data));
          // Move cursor after image
          quill.setSelection(range.index + 1);
        }

        setAlert({
          show: true,
          type: "success",
          message: "Gambar berhasil diupload!",
        });

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Hide alert after 2 seconds
        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
        }, 2000);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      setAlert({
        show: true,
        type: "danger",
        message: error.response?.data?.message || "Gagal mengupload gambar!",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  // Trigger file input click
  const handleInsertImageClick = () => {
    fileInputRef.current?.click();
  };

  // Handle thumbnail upload and preview
  const handleThumbnailChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setAlert({
        show: true,
        type: "danger",
        message: "Hanya file gambar yang diperbolehkan!",
      });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        show: true,
        type: "danger",
        message: "Ukuran file maksimal 5MB!",
      });
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload thumbnail
    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("type", "thumbnail");

      const response = await uploadBeritaImage(formData);

      if (response.data.success) {
        console.log("Thumbnail upload response:", response.data);
        setThumbnailFile(file);
        // Use functional update to ensure state is properly updated
        setCurrentBerita((prev) => {
          const updated = {
            ...prev,
            foto: response.data.data.filename,
          };
          console.log("Updated berita state with foto:", updated);
          return updated;
        });

        setAlert({
          show: true,
          type: "success",
          message: "Thumbnail berhasil diupload!",
        });

        setTimeout(() => {
          setAlert({ show: false, type: "", message: "" });
        }, 2000);
      }
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
      setAlert({
        show: true,
        type: "danger",
        message: error.response?.data?.message || "Gagal mengupload thumbnail!",
      });
      setThumbnailPreview("");
    } finally {
      setUploadingThumbnail(false);
    }
  };

  // Remove thumbnail
  const handleRemoveThumbnail = () => {
    setThumbnailFile(null);
    setThumbnailPreview("");
    // Use functional update to ensure state is properly updated
    setCurrentBerita((prev) => ({
      ...prev,
      foto: "",
    }));
  };

  // Toggle HTML source view
  const toggleHtmlSource = () => {
    if (showHtmlSource) {
      // Switching from HTML to Visual - apply HTML changes
      setCurrentBerita((prev) => ({
        ...prev,
        isiberita: htmlSource,
      }));
      setShowHtmlSource(false);
    } else {
      // Switching from Visual to HTML
      setHtmlSource(currentBerita.isiberita);
      setShowHtmlSource(true);
    }
  };

  // Handle HTML source change
  const handleHtmlSourceChange = (e) => {
    setHtmlSource(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setAlert({ show: false, type: "", message: "" });

    const user = getStoredUser();

    try {
      const dataToSend = {
        judul: currentBerita.judul,
        ringkasan:
          currentBerita.ringkasan || currentBerita.judul.substring(0, 200),
        isiberita: currentBerita.isiberita,
        foto: currentBerita.foto || null,
        pengirim: user?.userid || "Admin",
        jenisberita: currentBerita.jenisberita || "berita",
        judul_en: currentBerita.judul_en || null,
        ringkasan_en: currentBerita.ringkasan_en || null,
        isiberita_en: currentBerita.isiberita_en || null,
      };

      console.log("Submitting berita data:", dataToSend);
      console.log("Current berita state:", currentBerita);

      let response;
      if (editMode) {
        response = await updateBerita(currentBerita.id, dataToSend);
      } else {
        response = await createBerita(dataToSend);
      }

      if (response.data.success) {
        setAlert({
          show: true,
          type: "success",
          message: editMode
            ? "Berita berhasil diupdate!"
            : "Berita berhasil ditambahkan!",
        });

        setTimeout(() => {
          handleCloseModal();
          fetchBerita();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving berita:", error);
      console.error("Error response:", error.response?.data);
      setAlert({
        show: true,
        type: "danger",
        message:
          error.response?.data?.message ||
          "Terjadi kesalahan. Silakan coba lagi.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus berita ini?")) {
      return;
    }

    try {
      await deleteBerita(id);
      fetchBerita();
      setAlert({
        show: true,
        type: "success",
        message: "Berita berhasil dihapus!",
      });
      setTimeout(() => setAlert({ show: false, type: "", message: "" }), 3000);
    } catch (error) {
      setAlert({
        show: true,
        type: "danger",
        message: "Gagal menghapus berita.",
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="berita-management-page">
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-newspaper"></i>
            </div>
            <div>
              <h1 className="page-header-title">Kelola Berita</h1>
              <p className="page-header-subtitle">
                Manage berita dan artikel website
              </p>
            </div>
          </div>
          <Button
            variant="outline-light"
            className="btn-add"
            onClick={() => handleShowModal()}
          >
            <i className="fas fa-plus me-2"></i>
            Tambah Berita
          </Button>
        </div>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type}
          dismissible
          onClose={() => setAlert({ show: false, type: "", message: "" })}
        >
          {alert.message}
        </Alert>
      )}

      <Card className="table-card">
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover className="berita-table">
              <thead>
                <tr>
                  <th width="5%">ID</th>
                  <th width="10%">Thumbnail</th>
                  <th width="20%">Judul</th>
                  <th width="30%">Isi (Preview)</th>
                  <th width="8%">Hits</th>
                  <th width="12%">Tanggal</th>
                  <th width="10%">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {beritaList.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
                      <p className="text-muted">Belum ada berita</p>
                    </td>
                  </tr>
                ) : (
                  beritaList.map((berita) => {
                    console.log(
                      "Rendering berita row:",
                      berita.id,
                      "foto:",
                      berita.foto,
                    );
                    return (
                      <tr key={berita.id}>
                        <td>{berita.id}</td>
                        <td>
                          {berita.foto ? (
                            <img
                              src={`${BASE_URL}/uploads/${berita.foto}`}
                              alt={berita.judul}
                              className="berita-thumbnail"
                              onError={(e) => {
                                console.error(
                                  "Failed to load image:",
                                  e.target.src,
                                );
                                e.target.onerror = null;
                                e.target.src =
                                  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='75' viewBox='0 0 100 75'%3E%3Crect width='100' height='75' fill='%23e2e8f0'/%3E%3Ctext x='50' y='40' font-family='Arial' font-size='14' fill='%2394a3b8' text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";
                              }}
                            />
                          ) : (
                            <div className="berita-no-thumbnail">
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>{berita.judul}</strong>
                        </td>
                        <td>
                          <div
                            className="berita-preview"
                            dangerouslySetInnerHTML={{
                              __html:
                                berita.isiberita?.substring(0, 150) + "...",
                            }}
                          />
                        </td>
                        <td>
                          <span className="badge bg-info">
                            <i className="fas fa-eye me-1"></i>
                            {berita.hits || 0}
                          </span>
                        </td>
                        <td>{formatDate(berita.tanggal)}</td>
                        <td>
                          <div className="action-buttons">
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleShowModal(berita)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleDelete(berita.id)}
                              title="Delete"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal Form */}
      <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editMode
              ? `Edit Berita: ${currentBerita.judul}`
              : "Tambah Berita Baru"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
          {/* Action buttons di atas agar selalu terlihat */}
          <div className="d-flex gap-2 justify-content-end mb-3 pb-3 border-bottom">
            {alert.show && (
              <Alert
                variant={alert.type}
                className="mb-0 py-2 px-3 flex-grow-1"
                dismissible
                onClose={() => setAlert({ show: false, type: "", message: "" })}
              >
                {alert.message}
              </Alert>
            )}
            <Button variant="secondary" onClick={handleCloseModal}>
              Batal
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="berita-form"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-2"></i>
                  Simpan
                </>
              )}
            </Button>
          </div>

          <Form id="berita-form" onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Judul Berita *</Form.Label>
              <Form.Control
                type="text"
                name="judul"
                value={currentBerita.judul}
                onChange={handleChange}
                placeholder="Masukkan judul berita"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ringkasan</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="ringkasan"
                value={currentBerita.ringkasan}
                onChange={handleChange}
                placeholder="Ringkasan singkat berita (opsional, akan otomatis dari judul jika kosong)"
              />
              <Form.Text className="text-muted">
                Ringkasan singkat untuk preview berita
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Foto Thumbnail Berita</Form.Label>
              <div className="thumbnail-upload-container">
                {thumbnailPreview ? (
                  <div className="thumbnail-preview-wrapper">
                    <img
                      src={thumbnailPreview}
                      alt="Thumbnail preview"
                      className="thumbnail-preview"
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      className="btn-remove-thumbnail"
                      onClick={handleRemoveThumbnail}
                      disabled={uploadingThumbnail}
                    >
                      <i className="fas fa-times"></i>
                    </Button>
                  </div>
                ) : (
                  <div className="thumbnail-upload-box">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleThumbnailChange}
                      className="thumbnail-file-input"
                      id="thumbnailUpload"
                      disabled={uploadingThumbnail}
                    />
                    <label
                      htmlFor="thumbnailUpload"
                      className="thumbnail-upload-label"
                    >
                      {uploadingThumbnail ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-cloud-upload-alt fa-2x mb-2"></i>
                          <p className="mb-0">Klik untuk upload thumbnail</p>
                          <small className="text-muted">
                            PNG, JPG, GIF (Max 5MB)
                          </small>
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
              <Form.Text className="text-muted">
                Thumbnail akan ditampilkan di list berita (halaman admin &
                homepage). Ukuran rekomendasi: 800x600px atau 16:9 ratio.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Isi Berita (Indonesia) *</Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={toggleHtmlSource}
                  >
                    <i
                      className={`fas ${showHtmlSource ? "fa-eye" : "fa-code"} me-2`}
                    ></i>
                    {showHtmlSource ? "Visual Editor" : "HTML Source"}
                  </Button>
                  {!showHtmlSource && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleInsertImageClick}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-image me-2"></i>
                          Insert Gambar
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />
              {showHtmlSource ? (
                <Form.Control
                  as="textarea"
                  rows={20}
                  value={htmlSource}
                  onChange={handleHtmlSourceChange}
                  className="font-monospace html-source-editor"
                  placeholder="Edit HTML source..."
                  style={{ fontSize: "13px" }}
                />
              ) : (
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={currentBerita.isiberita}
                  onChange={handleEditorChange}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Tulis isi berita di sini..."
                />
              )}
              <Form.Text className="text-muted">
                {showHtmlSource
                  ? "Edit HTML source untuk mengatur ukuran gambar, contoh: <img src='...' width='500' height='300' style='display:block; margin:auto;' />"
                  : "Anda bisa copy-paste gambar langsung (Ctrl+V) atau klik tombol 'Insert Gambar'. Gunakan 'HTML Source' untuk mengatur ukuran gambar."}
              </Form.Text>
            </Form.Group>

            {/* ---- English Version ---- */}
            <hr />
            <h6 className="text-muted mb-3">
              <i className="fas fa-language me-2"></i>Versi Bahasa Inggris (Opsional)
            </h6>

            <Form.Group className="mb-3">
              <Form.Label>Title (English)</Form.Label>
              <Form.Control
                type="text"
                name="judul_en"
                value={currentBerita.judul_en || ""}
                onChange={handleChange}
                placeholder="Enter news title in English"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Summary (English)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="ringkasan_en"
                value={currentBerita.ringkasan_en || ""}
                onChange={handleChange}
                placeholder="Short summary in English (optional)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Content (English)</Form.Label>
              <div className="quill-en">
                <ReactQuill
                  theme="snow"
                  value={currentBerita.isiberita_en || ""}
                  onChange={handleEditorChangeEn}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write news content in English here..."
                />
              </div>
            </Form.Group>

            <div className="mt-2" />
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default BeritaManagement;
